import { getSignedUrl, sanitizeScene } from '@shoflo/polymer';
import { BROADCAST_UPDATE_SOURCE } from '../Types';
import { withCurrentParams } from '../utils';
import { showNotification } from '../index';
import { setProgram } from '../Broadcast';
import { setSourceUpdateStatus } from '@shoflo/studio-redux/actions';
import { UPDATE_PENDING } from '@shoflo/studio-redux/constants/SourceUpdateStatuses';
import { getSourceBannerLayer, updateBannerVariablesForLayer } from './utils';
import { shouldJoinWithoutKnock } from '../../utils/broadcastPresenterHelper';

/**
 *
 * @param sourceId
 * @param changes
 * @param contentUploadSLICallback
 * @param isS3ProxyFlagEnabled
 * @returns {(function(*, *, *): void)|*}
 */
function updateSource(
  sourceId,
  changes,
  contentUploadSLICallback,
  isS3ProxyFlagEnabled
) {
  return withCurrentParams(
    (dispatch, { teamId, eventId, broadcastId }, getState) => {
      const action = {
        type: BROADCAST_UPDATE_SOURCE,
        request: async apiClient => {
          try {
            dispatch(setSourceUpdateStatus(sourceId, UPDATE_PENDING));
            // To persist the updated source in the DB for other host/speakers
            let response = await apiClient.broadcast.updateSource(
              teamId,
              eventId,
              broadcastId,
              sourceId,
              changes
            );

            const updatedSource = isS3ProxyFlagEnabled
              ? response
              : await getSignedUrl(
                  {
                    ...response,
                    team: teamId,
                    event: eventId
                  },
                  apiClient
                );

            const { program } = getState();
            const joinWithoutKnockEnabled = shouldJoinWithoutKnock();

            // This ensures we only update the program with source when we are not trying to show or hide it
            // What changes should make us update program? its usually the source value
            const sourceInProgram = program.layers.find(
              layer => layer?.source?._id === sourceId
            );

            const shouldUpdateProgram =
              sourceInProgram && changes.hidden === undefined;

            let shouldUpdateBannerInProgram = false;
            let sourceBannerLayer = null;

            if (joinWithoutKnockEnabled) {
              sourceBannerLayer = getSourceBannerLayer(program, sourceId);
              shouldUpdateBannerInProgram = sourceBannerLayer !== undefined;
            }

            if (shouldUpdateProgram) {
              const updatedProgram = {
                ...program,
                layers: program.layers.map(layer => {
                  const { source } = layer;
                  // Actual source that was updated
                  if (source._id === sourceId) {
                    return {
                      ...layer,
                      source: {
                        ...updatedSource
                      }
                    };
                  }

                  // If the source has a banner layer, we need to update the banner variables
                  if (
                    shouldUpdateBannerInProgram &&
                    source._id === sourceBannerLayer.source._id
                  ) {
                    return updateBannerVariablesForLayer(layer, changes);
                  }

                  return layer;
                })
              };

              const scenePayload = sanitizeScene(
                JSON.parse(JSON.stringify(updatedProgram))
              );
              // To persist the program in the db for other host/speakers to see
              const updatedProgramResponse =
                await apiClient.broadcast.updateProgram(
                  teamId,
                  eventId,
                  broadcastId,
                  scenePayload
                );

              const programToBeUpdated = joinWithoutKnockEnabled
                ? updatedProgramResponse.program
                : updatedProgram;

              // To update the program in the store for the current speaker to see
              dispatch(setProgram(programToBeUpdated));
            }

            return updatedSource;
          } catch (error) {
            console.log('Failed to update source' + error);
            dispatch(
              showNotification('source update failed', {
                type: 'warning'
              })
            );
            throw error;
          }
        }
      };
      dispatch(action);
    }
  );
}

export default updateSource;
